import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Loader2, MousePointerSquareDashed, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { numa } from "@/lib/api";
import { useLLM } from "@/contexts/LLMContext";
import NumaMascot from "@/components/NumaMascot";

/**
 * Rewriting, on the text the author actually meant.
 *
 * Two things were wrong before. It never called a model - it showed the
 * chapter in a textarea and saved it back, so "migliora" changed nothing and
 * looked broken. And it always worked on the whole chapter, which is not what
 * someone who has just selected a paragraph is asking for.
 *
 * When the rewrite comes back identical, that is a result and it is said so.
 * Silence there reads as another failure.
 */

/** Compared ignoring whitespace and typographic quotes, which are not changes. */
function sameText(a = "", b = "") {
  const normalise = (value) =>
    value
      .replace(/[‘’“”]/g, "'")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  return normalise(a) === normalise(b);
}

export default function ImproveTextModal({
  open,
  onOpenChange,
  chapterTitle = "",
  projectType = "thesis",
  selection = null,      // { text } when the author had selected something
  wholeText = "",
  onApply,               // (improvedText, scope) => void
}) {
  const { ensureKey, reportError } = useLLM();
  const [stage, setStage] = useState("choose"); // choose | running | result | unchanged
  const [scope, setScope] = useState("selection");
  const [improved, setImproved] = useState("");
  const abortRef = useRef(null);

  const selected = (selection?.text || "").trim();
  const source = scope === "selection" ? selected : wholeText;

  useEffect(() => {
    if (!open) return;
    setImproved("");
    // With a selection there is nothing to ask: that is the text to work on.
    if (selected) {
      setScope("selection");
      setStage("choose");
    } else {
      setScope("chapter");
      setStage("choose");
    }
  }, [open, selected]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const run = async (which) => {
    const text = which === "selection" ? selected : wholeText;
    if (!text.trim()) {
      toast.error("Non c'è testo da migliorare.");
      return;
    }
    if (!(await ensureKey())) return;
    setScope(which);
    setStage("running");
    setImproved("");

    const controller = new AbortController();
    abortRef.current = controller;
    let collected = "";
    await numa.stream({
      action: "improve",
      text,
      chapter_title: chapterTitle,
      project_type: projectType,
      signal: controller.signal,
      onDelta: (delta) => {
        collected += delta;
        setImproved(collected);
      },
      onDone: () => {
        setStage(sameText(collected, text) ? "unchanged" : "result");
      },
      onError: (err) => {
        reportError(err);
        setStage("choose");
      },
    });
  };

  const apply = () => {
    onApply?.(improved.trim(), scope);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="improve-modal" className="max-w-3xl rounded-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display flex items-center gap-2 text-xl">
            <Sparkles className="h-4 w-4 text-assistant" />
            Migliora il testo
          </DialogTitle>
          <DialogDescription className="numa-meta">
            NUMA riscrive per precisione e scorrevolezza. Non aggiunge contenuti, dati o
            citazioni che non ci siano già.
          </DialogDescription>
        </DialogHeader>

        {stage === "choose" && (
          <div className="space-y-3" data-testid="improve-choose">
            {selected ? (
              <div className="rounded-md border border-line bg-surface-2 p-3.5">
                <p className="numa-eyebrow">Testo selezionato · {selected.split(/\s+/).length} parole</p>
                <p className="mt-1.5 line-clamp-3 text-[13.5px] leading-relaxed text-ink">
                  {selected.slice(0, 320)}
                  {selected.length > 320 ? "…" : ""}
                </p>
              </div>
            ) : (
              <div className="flex gap-3 rounded-md border border-line bg-surface-2 p-3.5">
                <MousePointerSquareDashed className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
                <p className="text-[13.5px] leading-relaxed text-ink">
                  Non hai selezionato niente. Seleziona la parte di testo da migliorare e
                  riapri, oppure lascia che NUMA lavori su tutta la sezione.
                </p>
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              {selected && (
                <Button data-testid="improve-selection" onClick={() => run("selection")}>
                  <Sparkles className="h-3.5 w-3.5" /> Migliora la selezione
                </Button>
              )}
              <Button
                variant={selected ? "outline" : "default"}
                data-testid="improve-whole"
                onClick={() => run("chapter")}
                disabled={!wholeText.trim()}
              >
                Migliora tutta la sezione
              </Button>
            </div>
          </div>
        )}

        {stage === "running" && (
          <div className="space-y-3" data-testid="improve-running">
            <p className="flex items-center gap-2 text-[13px] text-ink-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {scope === "selection" ? "Riscrittura della selezione…" : "Riscrittura della sezione…"}
            </p>
            <div className="max-h-[46vh] overflow-y-auto rounded-md border border-line bg-surface p-3.5 font-serif-display text-[14px] leading-relaxed text-ink">
              {improved || <span className="text-ink-subtle">…</span>}
            </div>
          </div>
        )}

        {stage === "unchanged" && (
          <div className="py-4 text-center" data-testid="improve-unchanged">
            <NumaMascot size={54} className="mx-auto" variant={3} />
            <p className="mt-3 font-serif-display text-[17px] font-semibold text-ink">
              Non c&apos;è niente da correggere qui.
            </p>
            <p className="numa-meta mx-auto mt-1.5 max-w-md leading-relaxed">
              NUMA ha riletto {scope === "selection" ? "la selezione" : "la sezione"} e non ha
              trovato nulla da cambiare: il testo regge già così. Va bene anche non riscrivere.
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <Button variant="outline" onClick={() => setStage("choose")}>
                Prova su un&apos;altra parte
              </Button>
              <Button onClick={() => onOpenChange(false)}>Chiudi</Button>
            </div>
          </div>
        )}

        {stage === "result" && (
          <div className="space-y-3" data-testid="improve-result">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="numa-eyebrow mb-1.5">Come l&apos;hai scritto</p>
                <div className="max-h-[38vh] overflow-y-auto rounded-md border border-line bg-surface-2 p-3 font-serif-display text-[13.5px] leading-relaxed text-ink-muted">
                  {source}
                </div>
              </div>
              <div>
                <p className="numa-eyebrow mb-1.5 text-assistant">Proposta di NUMA</p>
                <div className="max-h-[38vh] overflow-y-auto rounded-md border border-assistant/40 bg-assistant-soft/30 p-3 font-serif-display text-[13.5px] leading-relaxed text-ink">
                  {improved}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="numa-meta">
                {scope === "selection"
                  ? "Sostituisce solo la parte selezionata."
                  : "Sostituisce il testo dell'intera sezione."}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Lascia com&apos;è
                </Button>
                <Button data-testid="improve-apply" onClick={apply}>
                  <Check className="h-3.5 w-3.5" /> Applica
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
